const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/**
 * Seconds left on the countdown the current challenge shows for itself, or `null` when the
 * candidate is not looking at a challenge countdown at all (untimed challenge, a challenge
 * whose timer has not started yet, or no challenge on screen).
 *
 * @param {Object} stage the current stage from the assessment result process
 * @param {number} now current time in milliseconds
 * @returns {?number}
 */
function challengeSecondsRemaining(stage, now) {
  if (!stage || !stage.challengeId) return null;

  const { challenge, solution } = stage;
  if (!challenge || !solution || solution.pendingTimerReset) return null;

  if (challenge.isQuestionTimed()) {
    // Per-question quizzes have no single deadline, so we use the sum of every question's
    // limit: the longest the candidate could still be held on this challenge. An infinite
    // total means no question is actually limited, so there is no countdown to defer to.
    const totalMinutes = challenge.totalTimeLimitMin();
    return isFinite(totalMinutes) ? totalMinutes * SECONDS_PER_MINUTE : null;
  }

  if (!challenge.isTimed() || !solution.timerStartedAt) return null;

  const startedAt = new Date(solution.timerStartedAt).getTime();
  if (isNaN(startedAt)) return null;

  const expiresAt =
    startedAt + challenge.timeLimitMin * SECONDS_PER_MINUTE * MS_PER_SECOND;
  return (expiresAt - now) / MS_PER_SECOND;
}

/**
 * Decides whether the sidebar assessment timer should be shown.
 *
 * A candidate working inside a timed challenge already has that challenge's countdown in the
 * toolbar, and two competing deadlines are confusing — so we drop the assessment timer while
 * they are in there. The exception is the deadline that actually binds: if the assessment runs
 * out before the challenge would, that is the countdown worth watching, so it stays.
 *
 * @param {Object} params
 * @param {Object} params.timer the assessment timer (`assessmentTimerService`)
 * @param {Object} params.stage the current stage from the assessment result process
 * @param {number} [now] current time in milliseconds
 * @returns {boolean}
 */
export function shouldShowAssessmentTimer(
  { timer, stage } = {},
  now = Date.now(),
) {
  if (!timer) return false;

  const challengeRemaining = challengeSecondsRemaining(stage, now);

  // No challenge countdown on screen, so the assessment timer is the only one.
  if (challengeRemaining === null) return true;

  // The challenge countdown is spent; the assessment timer is useful again.
  if (challengeRemaining <= 0) return true;

  // A count-up timer has no deadline, so it can never be the one that binds.
  if (timer.isCountUp) return false;

  return timer.time <= challengeRemaining;
}
